import React, { useState } from 'react';
import './FAQPage.css';
import Footer from '../../components/Footer/Footer';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { AccordionSection, AccordionItem } from '../../components/shared/AccordionSection';
import { FilterBar, DESTINATION_OPTIONS, TYPE_OPTIONS } from '../../components/shared/FilterBar';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    id: '1',
    question: 'What are the check-in and check-out times?',
    answer: 'Check-in is from 3:00 PM and check-out is until 12:00 PM. Early check-in and late check-out may be available upon request and subject to availability.',
    category: 'General'
  },
  {
    id: '2',
    question: 'Are airport transfers included?',
    answer: 'Yes, complimentary airport transfers are included for all guests. Our team will coordinate with you prior to arrival to arrange your pickup from the nearest airport to each property.',
    category: 'Transportation'
  },
  {
    id: '3',
    question: 'What is included in my stay?',
    answer: 'Your stay includes private villa accommodation, all meals prepared by our chef, beverages including premium wines and spirits, daily private excursions with expert guides and 4WD vehicles, and access to all lodge facilities.',
    category: 'General'
  },
  {
    id: '4',
    question: 'What makes Awasi unique?',
    answer: 'Each Awasi lodge offers private villas, a dedicated guide and 4WD vehicle per villa, completely tailor-made excursions based on your interests, and an intimate setting with maximum privacy and personalized service.',
    category: 'General'
  },
  {
    id: '5',
    question: 'Can I combine multiple Awasi properties?',
    answer: 'Yes! We encourage guests to combine two or more lodges for the perfect South American adventure. We offer special packages with complimentary nights in connecting cities and seamless logistics between properties.',
    category: 'Booking'
  },
  {
    id: '6',
    question: 'How do private excursions work?',
    answer: 'Each villa has its own dedicated guide and 4WD vehicle. You decide what to do each day, when to depart, and how long to stay. Our guides create personalized itineraries based on your interests and fitness level.',
    category: 'Activities'
  },
  {
    id: '7',
    question: 'Can you accommodate dietary restrictions?',
    answer: 'Absolutely. Our chefs are experienced in accommodating all dietary requirements including vegetarian, vegan, gluten-free, kosher, and allergies. Please inform us of any dietary needs when booking.',
    category: 'Dining'
  },
  {
    id: '8',
    question: 'What is your cancellation policy?',
    answer: 'Cancellations made more than 60 days prior to arrival receive a full refund. Cancellations between 30-60 days receive 50% refund. Less than 30 days is non-refundable. We strongly recommend travel insurance.',
    category: 'Booking'
  },
  {
    id: '9',
    question: 'Are children welcome?',
    answer: 'Yes, we welcome families with children of all ages. We can arrange special activities, menus, and amenities for younger guests. Several lodges offer larger family villas.',
    category: 'General'
  },
  {
    id: '10',
    question: 'What should I pack?',
    answer: 'Packing varies by property and season. Generally: comfortable walking shoes, layers for changing weather, sun protection, and a camera. We provide a detailed packing list specific to your destination upon booking.',
    category: 'Preparation'
  },
  {
    id: '11',
    question: 'Is WiFi available?',
    answer: 'Yes, complimentary WiFi is available in all villas and common areas. Connection speeds may vary due to our remote locations, but are suitable for emails and basic browsing.',
    category: 'Amenities'
  },
  {
    id: '12',
    question: 'Do you offer spa services?',
    answer: 'Yes, spa services are available at all properties. Treatments include massages and therapies using local ingredients. Santa Catarina features a full Sisley Paris Spa, while other lodges offer in-villa treatments.',
    category: 'Amenities'
  },
  {
    id: '13',
    question: 'What is the best time to visit?',
    answer: 'Each property has its own seasonal highlights. Patagonia: October-April; Atacama: year-round; Iguazu: March-November; Santa Catarina: year-round; Mendoza: September-May. Our team can advise based on your interests.',
    category: 'Planning'
  },
  {
    id: '14',
    question: 'How do I get to each property?',
    answer: 'Atacama: Fly to Calama (CJC); Patagonia: Fly to Punta Arenas (PUQ); Iguazu: Fly to Puerto Iguazu (IGR); Santa Catarina: Fly to Florianópolis (FLN); Mendoza: Fly to Mendoza (MDZ). Transfers included.',
    category: 'Transportation'
  },
  {
    id: '15',
    question: 'What languages do staff speak?',
    answer: 'Our staff are fluent in English, Spanish, and Portuguese. Many team members also speak French, German, and Italian. We can arrange guides speaking specific languages with advance notice.',
    category: 'General'
  }
];

const FAQPage: React.FC = () => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [selectedProperty, setSelectedProperty] = useState<string | undefined>(undefined);

  const toggleItem = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const categories = ['All', ...Array.from(new Set(faqData.map(item => item.category)))];
  
  // Filter FAQs based on selected filters
  const filteredFAQs = faqData.filter(item => {
    // Filter by category
    if (selectedCategory && selectedCategory !== 'All') {
      if (item.category.toLowerCase() !== selectedCategory.toLowerCase()) {
        return false;
      }
    }
    
    // Property filtering could be added here when FAQ data includes property info
    if (selectedProperty) {
      // For now, just filter based on general applicability
      // This could be enhanced when FAQ data includes property-specific fields
    }
    
    return true;
  });

  return (
    <div className="faq-page">
      {/* Navigation */}
      <SmartNavigation 
        defaultVariant="light"
        scrolledVariant="light"
        autoSwitchOnScroll={false}
      />
      
      {/* Hero Section with Diptych */}
      <div className="faq-hero">
        <div className="faq-hero-text">
          <div className="faq-hero-content">
            <h1>Frequently Asked Questions</h1>
            <p>Find answers to common questions about your stay at Awasi. From booking details to on-site experiences, we're here to help you plan your perfect journey through South America's most stunning destinations.</p>
          </div>
          <div className="faq-hero-image-small"></div>
        </div>
        <div className="faq-hero-image"></div>
      </div>

      {/* FAQ Section */}
      <div className="faq-section">
        {/* Filters */}
        <FilterBar
          className="faq-filters"
          dropdowns={[
            {
              id: 'property',
              type: 'destination',
              level: 'brand',
              options: DESTINATION_OPTIONS,
              selectedValue: selectedProperty,
              onSelect: (value: string) => setSelectedProperty(value === selectedProperty ? undefined : value)
            },
            {
              id: 'category',
              type: 'type',
              level: 'brand',
              options: [
                { id: 'general', value: 'General', label: 'GENERAL' },
                { id: 'booking', value: 'Booking', label: 'BOOKING' },
                { id: 'activities', value: 'Activities', label: 'ACTIVITIES' },
                { id: 'dining', value: 'Dining', label: 'DINING' },
                { id: 'amenities', value: 'Amenities', label: 'AMENITIES' },
                { id: 'transportation', value: 'Transportation', label: 'TRANSPORTATION' },
                { id: 'planning', value: 'Planning', label: 'PLANNING' },
                { id: 'preparation', value: 'Preparation', label: 'PREPARATION' }
              ],
              selectedValue: selectedCategory,
              onSelect: (value: string) => setSelectedCategory(value === selectedCategory ? undefined : value)
            }
          ]}
          alignment="left"
        />

        {/* FAQ Items */}
        <AccordionSection
          className="faq-items"
          variant="expandable"
          items={filteredFAQs.map((item): AccordionItem => ({
            id: item.id,
            title: item.question,
            meta: {
              primary: item.category
            },
            expandable: true,
            expanded: expandedItems.has(item.id),
            onClick: () => toggleItem(item.id),
            content: <p>{item.answer}</p>
          }))}
          renderToggle={(expanded) => (
            <div className={`faq-toggle ${expanded ? 'expanded' : ''}`}>
              <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path d="M8.5 0V17M0 8.5H17" stroke="#49413d" strokeWidth="2"/>
              </svg>
            </div>
          )}
        />
      </div>

      {/* Full Image Section */}
      <div className="faq-full-image-section">
        <div className="faq-full-image"></div>
      </div>

      {/* Instagram Feed + Footer */}
      <InstagramSection />
      <Footer variant="default" />
    </div>
  );
};

export default FAQPage;